import { useEffect } from 'react';
import { graphql } from 'react-relay';

import { isIsolatedCloud } from '@atlassian/atl-context';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import type {
	useShowPublicLinkInShareSettingsQuery$variables,
	useShowPublicLinkInShareSettingsQuery$data,
} from './__generated__/useShowPublicLinkInShareSettingsQuery.graphql.ts';

const UseShowPublicLinkInShareSettingsQuery = graphql`
	query useShowPublicLinkInShareSettingsQuery(
		$spaceId: ID!
		$skipSpace: Boolean!
		$skipSite: Boolean!
	) {
		publicLinkSpace(spaceId: $spaceId) @skip(if: $skipSpace) {
			status
		}
		publicLinkSiteStatus @skip(if: $skipSite) {
			status
		}
	}
`;

// Can specify site and space settings to skip queries
export const useShowPublicLinkInShare = ({
	contentType,
	isSitePublicLinkEnabled,
	isPublicLinkEnabledInSpace,
	spaceId,
}: {
	contentType: string;
	isSitePublicLinkEnabled?: boolean;
	isPublicLinkEnabledInSpace?: boolean;
	spaceId?: string;
}): boolean => {
	const { data, error } = useQueryInterop<
		useShowPublicLinkInShareSettingsQuery$data,
		useShowPublicLinkInShareSettingsQuery$variables
	>(UseShowPublicLinkInShareSettingsQuery, {
		variables: {
			spaceId: spaceId ?? '',
			skipSpace: isPublicLinkEnabledInSpace !== undefined || spaceId === undefined,
			skipSite: isSitePublicLinkEnabled !== undefined,
		},
		skip: isPublicLinkEnabledInSpace !== undefined && isSitePublicLinkEnabled !== undefined,
	});

	useEffect(() => {
		if (error) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PERMISSIONS_EXPERIENCE,
			});
		}
	}, [error]);

	if (!(contentType === 'page' || contentType === 'whiteboard')) return false;

	// Most cases fall into this condition
	if (
		!isIsolatedCloud() ||
		!expValEquals('cc_perms_oasis_hide_public_link_entries', 'isEnabled', true)
	) {
		return true;
	}
	// Assertion: correct contentType and tenant is in an isolated cloud env (Gate is on)

	const actualSiteSetting: boolean =
		isSitePublicLinkEnabled === undefined
			? data?.publicLinkSiteStatus?.status === 'ON'
			: isSitePublicLinkEnabled;
	const actualSpaceSetting: boolean =
		isPublicLinkEnabledInSpace === undefined
			? data?.publicLinkSpace?.status === 'ON'
			: isPublicLinkEnabledInSpace;

	if (actualSiteSetting === false || actualSpaceSetting === false) {
		return false;
	}

	return true;
};
